import { Button } from "@/components/ui/button";
import CreateSupplierOnboardLink from "@/v2/api/supplier/CreateLink";
import { ArForm } from "@/v2/components/ui/form";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { ArUserCombobox } from "../../ui/combo-box/ArUser";
import { zodResolver } from "@hookform/resolvers/zod";
import { GenerateOnboardLinkSchema } from "@/v2/schema/supplier";

export default function SupplierOnboardLinkForm({
    closeOnSave,
}: {
    closeOnSave: () => void;
}) {
    const [generatedLink, setGeneratedLink] = useState<string>("");
    const [copied, setCopied] = useState(false);

    const form = useForm<GenerateOnboardLinkSchema>({
        resolver: zodResolver(GenerateOnboardLinkSchema),
        defaultValues: {
            user_id: "",
        },
    });

    const CreateMutation = useMutation({
        mutationFn: (data: GenerateOnboardLinkSchema) =>
            CreateSupplierOnboardLink(data),
    });

    const handleFormSubmit = (data: GenerateOnboardLinkSchema) => {
        CreateMutation.mutate(data, {
            onError: (err) => {
                console.error({ err });
                toast.error("Oops! Something went wrong", {
                    description: `While generating supplier onboard link, we encountered an issue.`,
                });
            },
            onSuccess: (data) => {
                const baseUrl = window.location.origin;
                const fullLink = `${baseUrl}/supplier/onboard/${data.Token}`;
                setGeneratedLink(fullLink);
                toast.success(`Generated supplier onboard link successfully`);
            },
        });
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(generatedLink);
            setCopied(true);
            toast.success("Link copied to clipboard!");
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            toast.error("Failed to copy link");
        }
    };

    const handleClose = () => {
        setGeneratedLink("");
        form.reset();
        closeOnSave();
    };

    const handleGenerateAnother = () => {
        setGeneratedLink("");
        form.reset();
        setCopied(false);
    };

    return (
        <ArForm
            form={form}
            formSchema={GenerateOnboardLinkSchema}
            onSubmit={handleFormSubmit}
        >
            <div className="space-y-4">
                <ArUserCombobox
                    name="user_id"
                    label="AR Coordinator"
                    defaultValue={null}
                />

                {generatedLink && (
                    <div className="space-y-2">
                        <label className="text-sm font-medium">
                            Generated Onboard Link
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={generatedLink}
                                readOnly
                                className="flex-1 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={handleCopyLink}
                                title="Copy link"
                            >
                                {copied ? (
                                    <Check className="h-4 w-4 text-green-600" />
                                ) : (
                                    <Copy className="h-4 w-4" />
                                )}
                            </Button>
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-2 border-t pt-4">
                    {generatedLink ? (
                        <>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleGenerateAnother}
                            >
                                Generate Another
                            </Button>
                            <Button type="button" onClick={handleClose}>
                                Done
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleClose}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="min-w-[160px]"
                                disabled={CreateMutation.isPending}
                            >
                                {CreateMutation.isPending
                                    ? "Generating..."
                                    : "Generate Link"}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </ArForm>
    );
}
